import { AlertCircle, CheckCircle, Copy, Link } from 'lucide-react';
import React, { useState } from 'react';
import { useDriveSysadmin } from '../../hooks/useSysadmin';

export function CopiarDrivePanel() {
  const { copyFileAndShare, isLoading, error } = useDriveSysadmin();

  const [destinationFolderId, setDestinationFolderId] = useState('');
  const [newFileName, setNewFileName] = useState('');
  const [emailsInput, setEmailsInput] = useState('');
  const [sourceFileId, setSourceFileId] = useState('');
  const [role, setRole] = useState<'reader' | 'writer' | 'commenter' | 'owner'>('reader');
  const [delayMs, setDelayMs] = useState<number>(150);

  const [result, setResult] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);

    const emailsList = emailsInput
      .split(/[\n,]/)
      .map((e) => e.trim())
      .filter((e) => e);

    if (emailsList.length === 0) {
      alert('Debes ingresar al menos un correo válido.');
      return;
    }

    if (!destinationFolderId || !newFileName) {
      alert('Carpeta destino y nombre de archivo son obligatorios.');
      return;
    }

    try {
      const res = await copyFileAndShare({
        destinationFolderId,
        newFileName,
        emails: emailsList,
        sourceFileId: sourceFileId || undefined,
        role,
        delayMs: delayMs || 150,
      });
      setResult(res);

      if (res.success) {
        setDestinationFolderId('');
        setNewFileName('');
        setEmailsInput('');
        setSourceFileId('');
      }
    } catch (err) {
      // El error ya es manejado por el hook, pero lo capturamos
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Copy className="w-5 h-5 text-blue-600" />
          Copiar y Compartir Archivo Drive
        </h2>
        <p className="text-gray-600 mt-1">
          Duplica un archivo de Google Drive en una carpeta específica y asígnale permisos de visualización/edición automáticamente.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Error en la operación</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Carpeta Destino <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={destinationFolderId}
              onChange={(e) => setDestinationFolderId(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="1tQANCgKYOnb_ux4yYsEBqnKs4bOj2t11"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre Nuevo Archivo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Ej. NOMBRE EMPRESA - REPORTE"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Correos para compartir <span className="text-red-500">*</span>
          </label>
          <textarea
            value={emailsInput}
            onChange={(e) => setEmailsInput(e.target.value)}
            required
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
          />
          <p className="text-xs text-gray-500 mt-1">Separa los correos por comas o saltos de línea.</p>
        </div>

        <div className="border-t border-gray-200 my-6 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">Opciones Avanzadas</h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID Archivo Origen
                <span className="text-gray-400 font-normal ml-2 text-xs">(Opcional)</span>
              </label>
              <input
                type="text"
                value={sourceFileId}
                onChange={(e) => setSourceFileId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Usa plantilla por defecto al dejar vacío"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol a conceder</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="reader">Lector (Reader)</option>
                <option value="commenter">Comentador (Commenter)</option>
                <option value="writer">Editor (Writer)</option>
                <option value="owner">Propietario (Owner)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delay Permisos (ms)</label>
              <input
                type="number"
                value={delayMs}
                onChange={(e) => setDelayMs(Number(e.target.value))}
                min={0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:bg-blue-300"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Copiando archivo...</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Ejecutar Clonación</span>
              </>
            )}
          </button>
        </div>
      </form>

      {result && result.success && (
        <div className="mt-8 bg-green-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-start gap-4">
            <CheckCircle className="w-8 h-8 text-green-600 shrink-0 mt-1" />
            <div className="w-full">
              <h3 className="text-xl font-bold text-green-800 mb-2">¡Archivo clonado exitosamente!</h3>

              <div className="my-4 p-4 bg-white rounded-lg border border-green-100 flex items-center justify-between shadow-sm">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Nuevo Archivo ID</p>
                  <p className="font-mono text-sm mt-1">{result.file?.id}</p>
                </div>
                <a
                  href={result.file?.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-colors"
                >
                  <Link className="w-4 h-4" />
                  Abrir Documento
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
                {result.sharing?.succeeded?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                      <CheckCircle className="w-4 h-4" /> Accesos Otorgados
                    </h4>
                    <ul className="space-y-2">
                      {result.sharing.succeeded.map((email: string) => (
                        <li key={email} className="text-sm bg-white border border-green-100 px-3 py-2 rounded-md">
                          {email}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.sharing?.failed?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                      <AlertCircle className="w-4 h-4" /> Errores al Compartir
                    </h4>
                    <ul className="space-y-2">
                      {result.sharing.failed.map((fail: any) => (
                        <li key={fail.email} className="text-sm bg-white border border-red-100 px-3 py-2 rounded-md">
                          <p className="font-medium text-red-700">{fail.email}</p>
                          <p className="text-xs text-red-500 mt-1">{fail.error}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
